import { useEffect, useState } from 'react';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Reveal `count` items one at a time on mount, returning how many are visible so
 * far (0 → count). Each item is unlocked `stepMs` after the previous, giving a
 * true one-by-one cascade rather than every item transitioning at once — the
 * caller drives each item's own CSS transition off `index < revealed`.
 *
 * Reduced-motion users get everything revealed immediately. The timers are torn
 * down on unmount, and the sequence restarts if `count` changes.
 */
export function useStaggeredReveal(count: number, stepMs = 90): number {
  // Reduced-motion (or a non-positive step) skips the cascade entirely.
  const instant = stepMs <= 0 || prefersReducedMotion();
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (instant) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= count; i++) {
      timers.push(setTimeout(() => setRevealed(i), i * stepMs));
    }
    return () => timers.forEach(clearTimeout);
  }, [count, stepMs, instant]);

  // In the instant case, report everything revealed straight from render (no
  // state churn); otherwise report how far the cascade has progressed. Clamp so
  // a shrunk `count` can never report more than exist.
  return instant ? count : Math.min(revealed, count);
}
